// BundleCarousel.js - UPDATED VERSION WITH SAVINGS DISPLAY
/**
 * BundleCarousel - Handles bundle display in two contexts:
 * 
 * 1. PRODUCT PAGE MODE (dynamic):
 *    - Variant changes based on user selection
 *    - Bundles update when variant changes
 *    - Listens to variant change events
 * 
 * 2. CART MODE (static):
 *    - Uses fixed variant from cart item (data-variant-id)
 *    - Bundles never change - locked to cart item's variant
 *    - Ignores all variant change events
 */
class BundleCarousel {
  constructor(container) {
    console.log('🚀 NEW BundleCarousel initializing...');
    console.log('🔍 Container element:', container);
    console.log('🔍 Container dataset:', container.dataset);
    console.log('🔍 data-variant-id:', container.dataset.variantId);
    console.log('🔍 data-product-id:', container.dataset.productId);
    console.log('🔍 data-bundle-id:', container.dataset.bundleId);
    console.log('🔍 data-max-bundles-visible:', container.dataset.maxBundlesVisible);
    
    this.container = container;
    this.bundleId = container.dataset.bundleId;
    this.bundleColumns = container.querySelectorAll('.bundle-column');
    this.hiddenItemsData = {};
    this.maxBundlesVisible = parseInt(container.dataset.maxBundlesVisible) || 3;
    
    // Add timeout tracking to prevent race conditions
    this.updateTimeouts = [];
    this.bindingInProgress = false;
    
    // Determine if this is a cart context (fixed variant) or product page context (dynamic variant)
    this.isCartContext = !!container.dataset.variantId;
    
    console.log('🚀 Initializing BundleCarousel for:', this.bundleId);
    console.log('📍 Context:', this.isCartContext ? 'CART (fixed variant)' : 'PRODUCT PAGE (dynamic variant)');
    console.log('📊 Max bundles visible:', this.maxBundlesVisible);

    try {
      const dataElement = container.querySelector(`.variant-bundle-data[data-bundle-id="${this.bundleId}"]`);
      this.bundleData = JSON.parse(dataElement?.textContent || '{}');
      console.log('📊 Bundle data loaded:', Object.keys(this.bundleData).length, 'variants');
    } catch (e) {
      console.error('❌ JSON Parse Error:', e);
      return;
    }

    this.currentVariantId = this.getCurrentVariantId();
    console.log('🎯 Locked to variant ID:', this.currentVariantId);
    this.init();
  }

  // Clear all pending timeouts to prevent race conditions
  clearPendingTimeouts() {
    this.updateTimeouts.forEach(timeout => clearTimeout(timeout));
    this.updateTimeouts = [];
  }

  getCurrentVariantId() {
    console.log('🔍 getCurrentVariantId() called');
    console.log('🔍 isCartContext:', this.isCartContext);
    
    if (this.isCartContext) {
      // Cart context: Use the fixed variant ID from the cart item
      const cartVariantId = this.container.dataset.variantId;
      console.log('🛒 CART MODE: Container variant ID:', cartVariantId);
      console.log('🛒 CART MODE: Using fixed variant ID:', cartVariantId);
      return cartVariantId;
    }

    // Product page context: Detect current variant dynamically
    console.log('📄 PRODUCT PAGE MODE: Detecting current variant...');
    
    const urlParams = new URLSearchParams(window.location.search);
    const variantFromUrl = urlParams.get('variant');
    console.log('🔍 URL variant:', variantFromUrl);
    if (variantFromUrl) {
      console.log('✅ Using URL variant ID:', variantFromUrl);
      return variantFromUrl;
    }

    const variantInput = document.querySelector('input[name="id"]');
    console.log('🔍 Input variant:', variantInput?.value);
    if (variantInput?.value) {
      console.log('✅ Using input variant ID:', variantInput.value);
      return variantInput.value;
    }

    const variantSelect = document.querySelector('select[name="id"]');
    console.log('🔍 Select variant:', variantSelect?.value);
    if (variantSelect?.value) {
      console.log('✅ Using select variant ID:', variantSelect.value);
      return variantSelect.value;
    }

    const fallback = Object.keys(this.bundleData)[0];
    console.log('🔍 Fallback variant ID:', fallback);
    return fallback;
  }

  formatPrice(priceInCents) {
    if (!priceInCents || isNaN(priceInCents)) return '0.00';
    return (parseFloat(priceInCents) / 100).toFixed(2);
  }

  init() {
    // Load bundles for the determined variant
    this.loadBundleForVariant(this.currentVariantId);
    
    // Only bind variant change listeners for product page context
    if (!this.isCartContext) {
      this.bindVariantChange();
      console.log('📄 Variant change listeners bound for product page');
    } else {
      console.log('🛒 CART MODE: Skipping variant change listeners - bundles are fixed');
    }
  }

  createBundleTotalFormHTML(bundleColumnId) {
    return `
      <div class="bundle-total-form">
        <button class="button add-bundle-to-cart" data-bundle-column-id="${bundleColumnId}">Add Bundle to Cart</button>
        <div class="bundle-pricing-wrapper">
          <p class="bundle-price">
            <span class="bundle-sale-price text-on-sale" data-bundle-column-id="${bundleColumnId}">$0.00</span>
            <span class="bundle-total text-subdued line-through" data-bundle-column-id="${bundleColumnId}">$0.00</span>
          </p>
          
          <!-- Savings Amount Display -->
          <p class="bundle-savings" data-bundle-column-id="${bundleColumnId}">
            <span class="savings-text">You Save: </span>
            <span class="savings-amount">$0.00</span>
          </p>
        </div>
        <button class="button add-bundle-product-to-cart" data-variant-id="" data-product-title="">Add Bundle to Cart</button>
      </div>
    `;
  }

  createAccordionStructure(components, bundleColumnId) {
    const needsAccordion = components.length > 3;
    
    if (!needsAccordion) {
      return components.map(component => this.createComponentHTML(component, bundleColumnId)).join('');
    }

    // Create HTML for ALL components, but mark which ones should be hidden initially
    let allItemsHTML = '';
    
    components.forEach((component, index) => {
      const isHidden = index >= 3;
      const hiddenClass = isHidden ? ' accordion-hidden-item' : '';
      const hiddenStyle = isHidden ? ' style="display: none;"' : '';
      
      allItemsHTML += `
        <div class="bundle-component-item${hiddenClass}" data-variant-id="${component.id}" data-price="${component.price}" data-bundle-column="${bundleColumnId}"${hiddenStyle}>
          <div class="component-image">
            <img src="${component.image || ''}" alt="${component.title}" loading="lazy">
          </div>
          <div class="component-right">
            <div class="component-top">
              <div class="component-titles">
                <h4><a href="/products/${component.handle}">${component.product_title}</a></h4>
                ${component.has_multiple_variants ? `<p class="variant-title">${component.title}</p>` : ''}
              </div>
              <p class="component-price">$${this.formatPrice(component.price)}</p>
            </div>
            <button type="button" class="toggle-button bundle-toggle active" data-variant-id="${component.id}" data-bundle-column="${bundleColumnId}">
              <span class="toggle-label">Remove</span>
            </button>
          </div>
        </div>
      `;
    });

    // Add the accordion toggle button
    allItemsHTML += `
      <div class="bundle-accordion-toggle" data-bundle-column="${bundleColumnId}">
        <div class="accordion__toggle bold">
          <div class="text-with-icon">
            <span>See all items (${components.length})</span>
          </div>
          <span class="circle-chevron">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </div>
      </div>
    `;

    return allItemsHTML;
  }

  createComponentHTML(component, bundleColumnId) {
    return `
      <div class="bundle-component-item" data-variant-id="${component.id}" data-price="${component.price}" data-bundle-column="${bundleColumnId}">
        <div class="component-image">
          <img src="${component.image || ''}" alt="${component.title}" loading="lazy">
        </div>
        <div class="component-right">
          <div class="component-top">
            <div class="component-titles">
              <h4><a href="/products/${component.handle}">${component.product_title}</a></h4>
              ${component.has_multiple_variants ? `<p class="variant-title">${component.title}</p>` : ''}
            </div>
            <p class="component-price">$${this.formatPrice(component.price)}</p>
          </div>
          <button type="button" class="toggle-button bundle-toggle active" data-variant-id="${component.id}" data-bundle-column="${bundleColumnId}">
            <span class="toggle-label">Remove</span>
          </button>
        </div>
      </div>
    `;
  }

  loadBundleForVariant(variantId) {
    console.log('🔍 loadBundleForVariant() called with:', variantId);
    console.log('🔍 isCartContext:', this.isCartContext);
    console.log('🔍 Available bundle data keys:', Object.keys(this.bundleData));
    console.log('🔍 Bundle data for variant ' + variantId + ':', this.bundleData[variantId]);
    console.log('🔍 Max bundles to display:', this.maxBundlesVisible);
    
    // Clear any pending timeouts to prevent race conditions
    this.clearPendingTimeouts();
    this.bindingInProgress = true;
    
    if (this.isCartContext) {
      console.log('🛒 CART MODE: Loading fixed bundles for cart item variant:', variantId);
      
      // CART FIX: Verify we have the line item key for upgrade functionality
      const lineItemKey = this.container.dataset.lineItemKey;
      console.log('🛒 CART MODE: Line item key:', lineItemKey);
      if (!lineItemKey) {
        console.error('🛒 ❌ CART MODE: Missing line item key - upgrade functionality will not work');
      }
    } else {
      console.log('📄 PRODUCT PAGE: Loading bundles for selected variant:', variantId);
    }
    
    const bundleData = this.bundleData[variantId] || {};
    const allBundles = bundleData.bundles || [];
    
    // Limit bundles based on maxBundlesVisible setting
    const bundles = allBundles.slice(0, this.maxBundlesVisible);
    
    console.log('🔍 Total bundles available:', allBundles.length);
    console.log('🔍 Bundles being displayed:', bundles.length);
    if (bundles.length > 0) {
      console.log('🔍 Bundle titles:', bundles.map(b => b.bundle_title));
    }

    // Track columns that have bundles
    const columnsWithBundles = [];

    this.bundleColumns.forEach((column, index) => {
      const title = column.querySelector('.bundle-dynamic-title');
      const image = column.querySelector('.bundle-dynamic-image');
      const list = column.querySelector('.bundle-components-list');
      const description = column.querySelector('.bundle_description');

      // Clear existing content
      if (title) title.textContent = '';
      if (image) {
        image.removeAttribute('src');
        image.removeAttribute('alt');
      }
      if (list) list.innerHTML = '';

      const bundle = bundles[index];
      console.log(`🔍 Bundle ${index + 1}:`, bundle?.bundle_title, '- Components:', bundle?.bundle_components?.length || 0);

      if (bundle?.bundle_components?.length > 0) {
        console.log(`✅ Showing bundle ${index + 1}: ${bundle.bundle_title}`);
        column.style.display = '';
        columnsWithBundles.push(column);
        
        if (title) title.textContent = bundle.bundle_title || '';
        if (description) description.textContent = bundle.bundle_description || '';
        if (image && bundle.featured_image) {
          image.setAttribute('src', bundle.featured_image);
          image.setAttribute('alt', bundle.bundle_title);
        }

        if (list) {
          const bundleColumnId = column.dataset.bundleColumnId;
          list.innerHTML = this.createAccordionStructure(bundle.bundle_components, bundleColumnId);
          
          // CRITICAL FIX: Update the bundle master product button data
          const bundleProductButton = column.querySelector('.add-bundle-product-to-cart');
          if (bundleProductButton) {
            console.log(`🔄 Updating bundle product button for column ${index + 1}:`);
            console.log(`   Bundle data:`, bundle);
            console.log(`   Old variant ID: ${bundleProductButton.dataset.variantId}`);
            console.log(`   New variant ID from bundle: ${bundle.bundle_master_variant_id}`);
            console.log(`   Bundle title: ${bundle.bundle_title}`);
            
            if (bundle.bundle_master_variant_id) {
              bundleProductButton.dataset.variantId = bundle.bundle_master_variant_id;
              bundleProductButton.dataset.productTitle = bundle.bundle_title || 'Bundle';
              
              // CART FIX: Ensure line item key is preserved for cart context
              if (this.isCartContext) {
                const containerLineItemKey = this.container.closest('[data-line-item-key]')?.dataset.lineItemKey;
                if (containerLineItemKey) {
                  bundleProductButton.dataset.lineItemKey = containerLineItemKey;
                  console.log(`🛒 CART: Added line item key to button: ${containerLineItemKey}`);
                } else {
                  console.log(`🛒 CART: No line item key found in container hierarchy`);
                }
              }
              
              console.log(`   ✅ Updated button variant ID to: ${bundle.bundle_master_variant_id}`);
            } else {
              console.log(`   ❌ No bundle_master_variant_id found in bundle data`);
            }
          } else {
            console.log(`   ❌ No bundle product button found in column ${index + 1}`);
          }
        }
      } else {
        console.log(`❌ Hiding bundle ${index + 1} - no components`);
        column.style.display = 'none';
      }
    });

    const anyVisible = columnsWithBundles.length > 0;
    const heading = this.container.querySelector('.bundle-heading');
    if (heading) heading.style.display = anyVisible ? '' : 'none';

    // FIXED: Single timeout for all binding operations
    const bindingTimeout = setTimeout(() => {
      console.log('🔄 Binding events for all columns...');
      
      // Bind toggle buttons for all columns with bundles
      columnsWithBundles.forEach(column => {
        const bundleColumnId = column.dataset.bundleColumnId;
        this.bindToggleButtonsForColumn(column);
        this.bindAccordionForColumn(column);
        this.updateColumnTotal(bundleColumnId);
      });
      
      // Bind add to cart buttons for the entire container
      this.bindAddToCartButtons();
      
      this.bindingInProgress = false;
      
      console.log('✅ All event binding completed for variant:', variantId);
      
      // Debug: Log the current state of all toggle buttons
      this.debugCurrentState();
      
    }, 50); // Single timeout, slightly longer to ensure DOM is ready

    this.updateTimeouts.push(bindingTimeout);

    if (this.isCartContext) {
      console.log('🛒 CART MODE: Bundles loaded and fixed - will not change');
    } else {
      console.log('📄 PRODUCT PAGE: Bundles loaded - will update with variant changes');
    }
  }

  // Debug method to log current state
  debugCurrentState() {
    console.log('🔍 DEBUG: Current bundle state for variant:', this.currentVariantId);
    this.bundleColumns.forEach((column, index) => {
      const bundleColumnId = column.dataset.bundleColumnId;
      const activeButtons = column.querySelectorAll('.bundle-toggle.active');
      const allButtons = column.querySelectorAll('.bundle-toggle');
      const bundleProductButton = column.querySelector('.add-bundle-product-to-cart');
      
      console.log(`🔍 Column ${index + 1} (${bundleColumnId}):`, {
        totalButtons: allButtons.length,
        activeButtons: activeButtons.length,
        variantIds: Array.from(activeButtons).map(btn => btn.dataset.variantId),
        bundleProductVariantId: bundleProductButton?.dataset.variantId,
        bundleProductTitle: bundleProductButton?.dataset.productTitle
      });
    });
  }

  bindAccordionForColumn(column) {
    const accordionToggles = column.querySelectorAll('.bundle-accordion-toggle');
    accordionToggles.forEach(toggle => {
      // Remove any existing event listeners
      const newToggle = toggle.cloneNode(true);
      toggle.parentNode.replaceChild(newToggle, toggle);
      
      newToggle.addEventListener('click', (e) => {
        e.preventDefault();
        const bundleColumnId = newToggle.dataset.bundleColumn;
        const hiddenItems = column.querySelectorAll('.accordion-hidden-item');
        const chevron = newToggle.querySelector('.circle-chevron');
        const textSpan = newToggle.querySelector('.text-with-icon span');
        
        const isExpanded = newToggle.classList.contains('expanded');
        
        if (isExpanded) {
          // Collapse: hide items and reset chevron
          hiddenItems.forEach(item => item.style.display = 'none');
          newToggle.classList.remove('expanded');
          if (chevron) chevron.style.transform = 'rotate(0deg)';
          
          // Get total items count for the original text
          const allItems = column.querySelectorAll('.bundle-component-item');
          if (textSpan) textSpan.textContent = `See all items (${allItems.length})`;
        } else {
          // Expand: show hidden items
          hiddenItems.forEach(item => item.style.display = '');
          newToggle.classList.add('expanded');
          if (chevron) chevron.style.transform = 'rotate(180deg)';
          if (textSpan) textSpan.textContent = 'See less items';
        }
      });
    });
  }

  bindToggleButtonsForColumn(column) {
    const buttons = column.querySelectorAll('.bundle-toggle');
    buttons.forEach(button => {
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);

      newButton.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = newButton.dataset.variantId;
        const bundleColumn = newButton.dataset.bundleColumn;
        newButton.classList.toggle('active');
        const isActive = newButton.classList.contains('active');
        const label = newButton.querySelector('.toggle-label');
        if (label) label.textContent = isActive ? 'Remove' : 'Add';
        newButton.style.backgroundColor = '#bb2f28';
        newButton.style.borderColor = '#bb2f28';
        newButton.style.color = 'white';
        newButton.setAttribute('title', isActive ? 'Click to remove from bundle' : 'Click to add to bundle');
        this.updateColumnTotal(bundleColumn);
      });
    });
  }

  updateColumnTotal(bundleColumnId) {
    const column = this.container.querySelector(`[data-bundle-column-id="${bundleColumnId}"]`);
    const originalPriceElement = column?.querySelector('.bundle-total');
    const salePriceElement = column?.querySelector('.bundle-sale-price');
    const savingsElement = column?.querySelector('.bundle-savings');
    const savingsAmountElement = column?.querySelector('.savings-amount');
    
    if (!originalPriceElement || !salePriceElement) return;
    
    // Get active buttons from both visible and hidden items
    const activeButtons = column.querySelectorAll('.bundle-toggle.active');
    let originalTotal = 0;
    
    activeButtons.forEach(btn => {
      const componentItem = btn.closest('.bundle-component-item');
      const price = parseInt(componentItem?.dataset.price || '0', 10);
      if (!isNaN(price)) originalTotal += price;
    });
    
    // Calculate 5% discount (sale price = 95% of original)
    const saleTotal = Math.round(originalTotal * 0.95);
    
    // Calculate savings amount
    const savingsTotal = originalTotal - saleTotal;
    
    // Update price displays with $ symbols
    originalPriceElement.textContent = '$' + this.formatPrice(originalTotal);
    salePriceElement.textContent = '$' + this.formatPrice(saleTotal);
    
    // Update savings display
    if (savingsElement && savingsAmountElement) {
      if (savingsTotal > 0) {
        savingsAmountElement.textContent = '$' + this.formatPrice(savingsTotal);
        savingsElement.style.display = 'block';
      } else {
        savingsElement.style.display = 'none';
      }
    }
    
    console.log(`💰 Bundle ${bundleColumnId} - Original: $${this.formatPrice(originalTotal)}, Sale: $${this.formatPrice(saleTotal)}, Savings: $${this.formatPrice(savingsTotal)}`);
  }

  bindAddToCartButtons() {
    // FIXED: Clear any existing event listeners first
    const existingButtons = this.container.querySelectorAll('.add-bundle-to-cart');
    existingButtons.forEach(button => {
      const newBtn = button.cloneNode(true);
      button.parentNode.replaceChild(newBtn, button);
      
      newBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        
        // Prevent double-clicks while binding is in progress
        if (this.bindingInProgress) {
          console.log('⏳ Binding in progress, please wait...');
          return;
        }
        
        const bundleColumnId = newBtn.dataset.bundleColumnId;
        const column = this.container.querySelector(`[data-bundle-column-id="${bundleColumnId}"]`);
        
        console.log('🛒 ADD TO CART CLICKED - Bundle Column ID:', bundleColumnId);
        console.log('🛒 Current variant ID:', this.currentVariantId);
        
        // Get ALL active buttons (both visible and hidden) AT THE TIME OF CLICKING
        const activeButtons = column.querySelectorAll('.bundle-toggle.active');
        console.log('🛒 Found active buttons:', activeButtons.length);
        
        // Debug: Show all buttons found
        activeButtons.forEach((btn, index) => {
          const item = btn.closest('.bundle-component-item');
          const productTitle = item.querySelector('.component-titles h4 a').textContent;
          const isHidden = item.classList.contains('accordion-hidden-item');
          console.log(`🛒 Item ${index + 1}: ${productTitle} (Hidden: ${isHidden}) - Variant ID: ${btn.dataset.variantId}`);
        });
        
        const items = Array.from(activeButtons).map(btn => ({
          id: parseInt(btn.dataset.variantId, 10),
          quantity: 1
        }));
        
        console.log('🛒 FINAL items to add to cart:', items);
        
        // Additional verification logging
        console.log('🛒 VERIFICATION - Current variant ID:', this.currentVariantId);
        console.log('🛒 VERIFICATION - Bundle column element:', column);
        console.log('🛒 VERIFICATION - Bundle data for current variant:', this.bundleData[this.currentVariantId]);
        
        if (!items.length) {
          alert('Please select at least one item from this bundle.');
          return;
        }

        newBtn.disabled = true;
        const originalText = newBtn.textContent;
        newBtn.textContent = 'Adding...';

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items })
          });
          
          if (!res.ok) {
            const errorData = await res.json();
            throw errorData;
          }
          
          const responseData = await res.json();
          console.log('🛒 ✅ Successfully added to cart:', responseData);
          
          document.dispatchEvent(new CustomEvent('variant:add', { detail: { items } }));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          newBtn.textContent = 'Added!';
          
          setTimeout(() => {
            newBtn.disabled = false;
            newBtn.textContent = originalText;
          }, 2000);
        } catch (error) {
          console.error('🛒 ❌ Error adding to cart:', error);
          alert(error?.message || error?.description || 'Error adding items to cart.');
          newBtn.disabled = false;
          newBtn.textContent = originalText;
        }
      });
    });

    // Add bundle *product* button
    const bundleProductButtons = this.container.querySelectorAll('.add-bundle-product-to-cart');
    bundleProductButtons.forEach(button => {
      const newBtn = button.cloneNode(true);
      button.parentNode.replaceChild(newBtn, button);

      newBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = parseInt(newBtn.dataset.variantId, 10);
        const title = newBtn.dataset.productTitle;
        const lineItemKey = newBtn.dataset.lineItemKey;
        
        console.log('🔍 BUNDLE PRODUCT BUTTON DEBUG:');
        console.log('🔍 Button element:', newBtn);
        console.log('🔍 Button dataset:', newBtn.dataset);
        console.log('🔍 data-variant-id:', newBtn.dataset.variantId);
        console.log('🔍 data-product-title:', newBtn.dataset.productTitle);
        console.log('🔍 data-line-item-key:', newBtn.dataset.lineItemKey);
        console.log('🔍 Current carousel variant ID:', this.currentVariantId);
        console.log('🔍 variantId variable:', variantId);
        console.log('🔍 lineItemKey variable:', lineItemKey);
        console.log('🔍 Has line item key?', !!lineItemKey);
        
        const isUpgrade = !!lineItemKey;

        if (isNaN(variantId)) {
          alert('Invalid variant ID');
          return;
        }

        console.log('🛒 Bundle Product Action:', isUpgrade ? 'UPGRADE' : 'ADD');
        console.log('🛒 Bundle Product Variant ID:', variantId);
        if (isUpgrade) {
          console.log('🛒 Removing line item key:', lineItemKey);
        }

        newBtn.disabled = true;
        const originalText = newBtn.textContent;
        newBtn.textContent = isUpgrade ? 'Upgrading...' : 'Adding...';

        try {
          // Step 1: Remove existing item if this is an upgrade
          if (isUpgrade && lineItemKey) {
            console.log('🛒 Step 1: Removing current item...');
            const removeRes = await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({
                updates: {
                  [lineItemKey]: 0
                }
              })
            });
            
            if (!removeRes.ok) {
              const removeError = await removeRes.json();
              throw new Error(removeError.message || 'Failed to remove current item');
            }
            
            console.log('🛒 ✅ Current item removed successfully');
          }

          // Step 2: Add the bundle product
          console.log('🛒 Step 2: Adding bundle product...');
          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1 }]
            })
          });

          if (!addRes.ok) {
            const addError = await addRes.json();
            throw new Error(addError.message || 'Failed to add bundle product');
          }

          console.log('🛒 ✅ Bundle product added successfully');

          document.dispatchEvent(new CustomEvent('variant:add', { detail: { items: [{ id: variantId }] } }));
          document.dispatchEvent(new CustomEvent('cart:refresh'));

          newBtn.textContent = isUpgrade ? 'Upgraded!' : 'Added!';
          
          if (isUpgrade) {
            setTimeout(() => {
              // Optional: close cart drawer after upgrade
            }, 1500);
          }
          
          setTimeout(() => {
            newBtn.disabled = false;
            newBtn.textContent = originalText;
          }, 2000);

        } catch (err) {
          console.error('🛒 ❌ Error during bundle product action:', err);
          alert(err?.message || `Couldn't ${isUpgrade ? 'upgrade to' : 'add'} ${title}`);
          newBtn.disabled = false;
          newBtn.textContent = originalText;
        }
      });
    });
  }

  bindVariantChange() {
    // This method should only be called for product page context
    console.log('📄 PRODUCT PAGE: Binding variant change listeners');

    document.addEventListener('variant:change', (event) => {
      const variantId = event.detail?.variant?.id;
      if (variantId && variantId.toString() !== this.currentVariantId) {
        console.log('📄 PRODUCT PAGE: Variant changed via event to:', variantId);
        this.currentVariantId = variantId.toString();
        this.loadBundleForVariant(this.currentVariantId);
      }
    });

    document.querySelectorAll('select[name="id"], input[name="id"]').forEach(input => {
      input.addEventListener('change', (e) => {
        const variantId = e.target.value;
        if (variantId && variantId !== this.currentVariantId) {
          console.log('📄 PRODUCT PAGE: Variant selector changed to:', variantId);
          this.currentVariantId = variantId;
          // Clear pending timeouts before loading new variant
          this.clearPendingTimeouts();
          this.loadBundleForVariant(variantId);
        }
      });
    });
  }
}

function initializeBundleCarousels(container = document) {
  console.log('🔄 Initializing bundle carousels in:', container);
  const sections = container.querySelectorAll('.bundle_component_section');
  console.log('🔍 Found', sections.length, 'bundle sections');
  
  sections.forEach((section, index) => {
    console.log(`🎯 Initializing section ${index + 1}:`, section.dataset.bundleId);
    
    // Prevent duplicate initialization
    if (section._bundleCarouselInstance) {
      console.log(`⚠️ Section already has bundle carousel instance, skipping`);
      return;
    }
    
    try {
      const instance = new BundleCarousel(section);
      section._bundleCarouselInstance = instance; // Store reference to prevent duplicates
    } catch (error) {
      console.error('❌ Error initializing bundle carousel:', error);
    }
  });
}

// Enhanced cart-specific initialization
function initializeCartBundles() {
  console.log('🛒 Initializing cart bundles...');
  
  // Clear any existing instances in cart context
  const cartSections = document.querySelectorAll('.cart-bundle-wrapper .bundle_component_section');
  cartSections.forEach(section => {
    if (section._bundleCarouselInstance) {
      console.log('🛒 Clearing existing cart bundle instance');
      section._bundleCarouselInstance = null;
    }
  });
  
  // Re-initialize all cart bundles
  const cartContainers = document.querySelectorAll('.cart-bundle-wrapper');
  cartContainers.forEach(wrapper => {
    console.log('🛒 Initializing cart bundle wrapper:', wrapper.dataset);
    initializeBundleCarousels(wrapper);
  });
}

// Debounced cart update handler to prevent multiple rapid calls
let cartUpdateTimeout;
function handleCartUpdate() {
  clearTimeout(cartUpdateTimeout);
  cartUpdateTimeout = setTimeout(() => {
    console.log('🛒 Cart update detected - reinitializing bundles');
    initializeCartBundles();
  }, 300);
}

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    console.log('📄 DOM Content Loaded - Initializing bundles');
    initializeBundleCarousels();
  });
} else {
  console.log('📄 Document already ready - Initializing bundles');
  initializeBundleCarousels();
}

// Handle Shopify section reloads
document.addEventListener('shopify:section:load', (e) => {
  console.log('🔄 Shopify section loaded - Re-initializing bundles');
  initializeBundleCarousels(e.target);
});

// Enhanced cart event handlers
document.addEventListener('cart:open', () => {
  console.log('🛒 Cart opened - Initializing cart bundles');
  setTimeout(initializeCartBundles, 100);
});

document.addEventListener('drawer:open', () => {
  console.log('🛒 Drawer opened - Initializing cart bundles');
  setTimeout(initializeCartBundles, 100);
});

// Handle cart updates (item removal, quantity changes)
document.addEventListener('cart:refresh', handleCartUpdate);
document.addEventListener('cart:update', handleCartUpdate);
document.addEventListener('cart:updated', handleCartUpdate);

// Handle variant add events (might trigger cart updates)
document.addEventListener('variant:add', () => {
  setTimeout(handleCartUpdate, 200);
});

// Listen for cart drawer DOM changes (for when items are removed)
const observeCartChanges = () => {
  const cartDrawer = document.querySelector('.cart-drawer, [data-cart-drawer], .drawer--cart');
  if (cartDrawer) {
    const observer = new MutationObserver((mutations) => {
      const hasCartItemChanges = mutations.some(mutation => 
        Array.from(mutation.removedNodes).some(node => 
          node.nodeType === 1 && (
            node.matches?.('.cart-item, [data-cart-item], .line-item') ||
            node.querySelector?.('.cart-item, [data-cart-item], .line-item')
          )
        ) ||
        Array.from(mutation.addedNodes).some(node => 
          node.nodeType === 1 && (
            node.matches?.('.cart-item, [data-cart-item], .line-item') ||
            node.querySelector?.('.cart-item, [data-cart-item], .line-item')
          )
        )
      );
      
      if (hasCartItemChanges) {
        console.log('🛒 Cart DOM changes detected');
        handleCartUpdate();
      }
    });
    
    observer.observe(cartDrawer, {
      childList: true,
      subtree: true
    });
    
    console.log('🛒 Cart change observer initialized');
  }
};

// Initialize cart observer when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeCartChanges);
} else {
  observeCartChanges();
}

// Global exposure
window.BundleCarousel = BundleCarousel;
window.initializeBundleCarousels = initializeBundleCarousels;
window.initializeCartBundles = initializeCartBundles;